import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useState } from "react";

import type { FeelingAtHomeWidgetSuggestionDto } from "@/api/types";
import { AnalyticsWidgetCard } from "@/components/Analytics/AnalyticsWidgetCard";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { Lightning02Icon } from "@/components/Icons/Lightning02Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { formatDistance } from "@/helpers/date";
import { twResolve } from "@/helpers/tw-resolve";
import { useBool } from "@/hooks/useBool";
import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { canListAnySurvey } from "@/modules/surveys/permissions";
import { analyticsMutations, useAnalyticsQueries } from "@/queries/analytics";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { HomeFahSuggestionWidgetDetailsModal } from "./HomeFahSuggestionWidgetDetailsModal";

const FAH_SUGGESTION_LAST_READ_AT = "fah-suggestion-last-read-at";
const FALLBACK_DATA: FeelingAtHomeWidgetSuggestionDto[] = [
  {
    id: "1",
    title: "-",
    description: "-",
    tips: [],
  },
  {
    id: "2",
    title: "-",
    description: "-",
    tips: [],
  },
  {
    id: "3",
    title: "-",
    description: "-",
    tips: [],
  },
] satisfies FeelingAtHomeWidgetSuggestionDto[];
export function HomeFahFollowUpSuggestionWidget(): React.ReactNode {
  const [isDetailsModalOpened, detailsModalHandler] = useBool(false);
  const [activeSuggestionId, setActiveSuggestionId] = useState<string | undefined>(undefined);
  const [isHighlighted, setIsHighlighted] = useState(false);

  const hasPermission = usePermission();
  const { t } = useTranslation();
  const slug = useSlug();
  const analyticsQueries = useAnalyticsQueries();
  const {
    data: fahSuggestions,
    isPending: isPendingFahSuggestions,
    isError: isErrorFahSuggestions,
    error: errorFahSuggestions,
  } = useQuery(analyticsQueries.getFahSuggestions());
  const updateFahSuggestionReadMutation = analyticsMutations.useUpdateFahSuggestionRead();
  const handleOpenSuggestion = (suggestion: FeelingAtHomeWidgetSuggestionDto) => {
    setActiveSuggestionId(suggestion.id);
    detailsModalHandler.setTrue();
    if (!suggestion.viewedAt) {
      updateFahSuggestionReadMutation.mutate({
        suggestionId: suggestion.id,
      });
    }
  };
  const isError = isErrorFahSuggestions;
  const isPending = isPendingFahSuggestions;
  const error = errorFahSuggestions;

  // Highlight widget when there are new suggestions
  useEffect(() => {
    if (!fahSuggestions?.lastUpdatedAt || !fahSuggestions?.suggestions) {
      return;
    }

    const lastReadAt: string | undefined = getLocalStorageValue(FAH_SUGGESTION_LAST_READ_AT, undefined);
    if (!lastReadAt || new Date(fahSuggestions.lastUpdatedAt) > new Date(lastReadAt)) {
      setIsHighlighted(true);
    }

    // Update the last read date to now
    updateLocalStorage(FAH_SUGGESTION_LAST_READ_AT, new Date().toISOString());
  }, [fahSuggestions?.lastUpdatedAt, fahSuggestions?.suggestions]);

  // If BE returns `undefined` instead of an empty array, this implies that the project does not yet have no FAH data.
  // Therefore we hide the widget
  if (!isError && !isPending && !fahSuggestions.suggestions) {
    return null;
  }

  // This indicates whether there are any suggestions available when the project SHOULD have sufficient FAH data.
  const isDataAvailable = !!fahSuggestions?.suggestions && fahSuggestions.suggestions.length > 0;
  const suggestions = isDataAvailable ? fahSuggestions.suggestions! : FALLBACK_DATA;

  return (
    <>
      <AnalyticsWidgetCard.Root
        title={t("component.analytics-widget.fah-suggestions.title")}
        description={t("component.analytics-widget.fah-suggestions.description")}
        isError={isError}
        error={error}
        isLoading={isPending}
        isDataAvailable={isDataAvailable}
        href={isDataAvailable && hasPermission(canListAnySurvey) ? routes.surveys.fah({ slug }) : undefined}
        isHighlighted={isHighlighted}
      >
        <AnalyticsWidgetCard.Body>
          <div className="grid w-full grid-cols-1 gap-2">
            {suggestions?.map((suggestion) => (
              <button
                type="button"
                onClick={() => handleOpenSuggestion(suggestion)}
                key={suggestion.id}
                className={twResolve(
                  "flex w-full items-center gap-4 rounded-lg border border-aop-basic-blue-100 bg-aop-basic-blue-100 p-4 transition-colors hover:bg-aop-basic-blue-200",
                  suggestion.viewedAt && "border-grey-100 bg-white hover:bg-aop-basic-blue-100",
                )}
              >
                <div className="flex flex-1 flex-col">
                  <span className="text-left text-headline4 xl:text-headline3">{suggestion.title}</span>
                  <span className="text-left text-caption xl:text-body">{suggestion.description}</span>
                </div>
                <ChevronRightIcon className="text-grey-700" size={24} />
              </button>
            ))}
          </div>
        </AnalyticsWidgetCard.Body>
        <AnalyticsWidgetCard.Footer>
          <div className="flex w-full flex-col gap-2">
            <hr className="h-px w-full text-grey-100" />
            <div className="flex w-full flex-wrap items-center justify-between gap-2 text-grey-700">
              <div className="inline-flex items-center gap-1">
                <Lightning02Icon />
                <p>{t("common.label.generated-by-ai")}</p>
              </div>
              {fahSuggestions?.lastUpdatedAt && (
                <Tooltip tooltip={t("component.analytics-widget.fah-suggestions.tooltip.last-updated")}>
                  <time className="whitespace-nowrap">
                    {t("common.label.last-updated", {
                      distance: formatDistance(t, {
                        start: new Date(fahSuggestions.lastUpdatedAt),
                        end: new Date(),
                      }),
                    })}
                  </time>
                </Tooltip>
              )}
            </div>
          </div>
        </AnalyticsWidgetCard.Footer>
      </AnalyticsWidgetCard.Root>
      <HomeFahSuggestionWidgetDetailsModal
        suggestion={suggestions.find((currSuggestion) => currSuggestion.id === activeSuggestionId)}
        isOpened={isDetailsModalOpened && Boolean(activeSuggestionId)}
        onOpenChange={detailsModalHandler.set}
      />
    </>
  );
}
